import styled, { css } from 'styled-components';
import { defaultTheme } from '@styles/theme.styled';
import { generateScales, generateScalingQueries } from '@helpers/generate-media-queries';

export const StyledTitle = styled.h1<{
  $margin: keyof typeof defaultTheme.spacing;
  $fontSize: keyof typeof defaultTheme.fontSize;
  $fontColor: keyof typeof defaultTheme.colors;
  $charCount?: number;
}>`
  ${({ theme, $margin, $fontSize, $fontColor, $charCount }) => {
    const bottomMargin: string = $margin === 'none' ? '0rem' : theme.spacing[$margin];

    let heading1Styles = null;
    if ($fontSize === 'heading1') {
      if ($charCount && $charCount <= 10) {
        heading1Styles = css`
          font-size: 4.5rem;
          @media only screen and (max-width: ${theme.breakpoints.xxl}) {
            font-size: 4.25rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.xl}) {
            font-size: 3.75rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.lg}) {
            font-size: 3.5rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.md}) {
            font-size: 3rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.sm}) {
            font-size: 2.5rem;
          }
        `;
      } else if ($charCount && $charCount > 24) {
        heading1Styles = css`
          font-size: 3.5rem;
          @media only screen and (max-width: ${theme.breakpoints.xl}) {
            font-size: 3.25rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.lg}) {
            font-size: 3rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.md}) {
            font-size: 2.5rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.sm}) {
            font-size: 2.125rem;
          }
        `;
      } else {
        heading1Styles = css`
          font-size: 4rem;
          @media only screen and (max-width: ${theme.breakpoints.xl}) {
            font-size: 3.5rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.lg}) {
            font-size: 3rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.md}) {
            font-size: 2.5rem;
          }
          @media only screen and (max-width: ${theme.breakpoints.sm}) {
            font-size: 2.125rem;
          }
        `;
      }
    }

    return css`
      margin: 0 0 ${$margin === 'none' ? 0 : theme.spacing[$margin]};
      font-family: ${theme.fontFamily.display};
      font-weight: ${theme.fontWeights.bold};
      line-height: ${theme.lineHeights.sm};
      color: ${theme.colors[$fontColor]};

      ${heading1Styles}

      ${$fontSize === 'heading2' &&
      css`
        font-size: 3rem;

        @media only screen and (max-width: ${theme.breakpoints.xl}) {
          font-size: 2.5rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.lg}) {
          font-size: 2.25rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.md}) {
          font-size: 2rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.sm}) {
          font-size: 1.875rem;
        }
      `}

      ${$fontSize === 'heading3' &&
      css`
        font-size: 2.5rem;

        @media only screen and (max-width: ${theme.breakpoints.xl}) {
          font-size: 2.25rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.lg}) {
          font-size: 2rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.md}) {
          font-size: 1.875rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.sm}) {
          font-size: 1.75rem;
        }
      `}

      ${$fontSize === 'heading4' &&
      css`
        font-size: 2rem;

        @media only screen and (max-width: ${theme.breakpoints.xl}) {
          font-size: 1.9375rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.lg}) {
          font-size: 1.875rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.md}) {
          font-size: 1.75rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.sm}) {
          font-size: 1.5rem;
        }
      `}

      ${$fontSize === 'heading5' &&
      css`
        font-size: 1.5rem;

        @media only screen and (max-width: ${theme.breakpoints.lg}) {
          font-size: 1.375rem;
        }

        @media only screen and (max-width: ${theme.breakpoints.sm}) {
          font-size: 1.25rem;
        }
      `}

      ${$fontSize === 'heading6' &&
      css`
        font-size: 1.25rem;

        @media only screen and (max-width: ${theme.breakpoints.md}) {
          font-size: 1.125rem;
        }
      `}

      ${($fontSize === 'subheading' || $fontSize === 'small' || $fontSize === 'body') &&
      css`
        ${generateScales(
          'font-size',
          theme.fontSize[$fontSize].mobile,
          theme.fontSize[$fontSize].desktop,
          theme
        )}
      `}

      margin-bottom: ${bottomMargin};
      ${generateScalingQueries('margin-bottom', bottomMargin, theme)}
    `;
  }}
`;
